import React, { useEffect, useRef, useState } from "react";

import { motion } from "framer-motion";
import { twMerge } from "tailwind-merge";

type Option = {
  label: string;
  value: string;
};

type SegmentedControlProps = {
  options: Option[];
  initialValue?: string;
  value?: string;
  onChange: (value?: string) => void;
  disabled?: boolean;
  className?: string;
  segmentClassName?: (selected: boolean) => string;
  highlightClassName?: string;
};

const SegmentedControl: React.FC<SegmentedControlProps> = ({
  options,
  initialValue,
  value,
  onChange,
  disabled = false,
  className,
  segmentClassName,
  highlightClassName,
}) => {
  const [selected, setSelected] = useState<string | undefined>(initialValue);
  const [userInteracted, setUserInteracted] = useState(false);
  const segmentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const controlRef = useRef<HTMLDivElement>(null);
  const [highlightStyle, setHighlightStyle] = useState({
    width: 0,
    position: 0,
    opacity: 0,
  });

  useEffect(() => {
    if (!userInteracted || value !== selected) {
      const newValue = value || initialValue;
      const selectedIndex = options.findIndex(
        (option) => option.value === newValue,
      );
      setSelected(newValue);
      updateHighlight(selectedIndex);
    }
  }, [value, initialValue, options]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const selectedIndex = options.findIndex(
      (option) => option.value === selected,
    );
    updateHighlight(selectedIndex);
  }, [options, selected]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (disabled || !controlRef.current?.contains(document.activeElement))
        return;
      const selectedIndex = options.findIndex(
        (option) => option.value === selected,
      );
      if (event.key === "ArrowLeft") {
        const prevIndex = selectedIndex - 1;
        if (prevIndex >= 0) {
          handleSelect(options[prevIndex].value, prevIndex);
        }
      } else if (event.key === "ArrowRight") {
        const nextIndex = selectedIndex + 1;
        if (nextIndex < options.length) {
          handleSelect(options[nextIndex].value, nextIndex);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [disabled, options, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateHighlight = (selectedIndex: number) => {
    if (selectedIndex === -1) {
      setHighlightStyle({
        width: 0,
        position: 0,
        opacity: 0,
      });
      return;
    }
    const widths = segmentRefs.current.map((ref) => ref?.offsetWidth || 0);
    const selectedWidth = widths[selectedIndex];
    const position = widths
      .slice(0, selectedIndex)
      .reduce((acc, width) => acc + width, 0);

    setHighlightStyle({
      width: selectedWidth,
      position,
      opacity: 1,
    });
  };

  const handleSelect = (value: string, index: number) => {
    if (disabled || value === selected) return;
    setUserInteracted(true);
    setSelected(value);
    onChange(value);
    updateHighlight(index);
  };

  const getSegmentClassNames = (optionValue: string) =>
    twMerge(
      "z-10 cursor-pointer select-none text-nowrap px-2 py-0",
      segmentClassName && segmentClassName(selected === optionValue),
      disabled && "pointer-events-none",
    );

  return (
    <div
      ref={controlRef}
      className={twMerge(
        "relative flex items-center justify-start overflow-hidden rounded-md border-2 border-gray-300 bg-gray-100 p-1",
        disabled ? "cursor-not-allowed opacity-50" : "",
        className,
      )}
    >
      <Highlight {...highlightStyle} className={highlightClassName} />
      {options.map((option, index) => (
        <Segment
          key={option.value}
          ref={(el) => (segmentRefs.current[index] = el)}
          className={getSegmentClassNames(option.value)}
          onClick={() => handleSelect(option.value, index)}
          label={option.label}
          selected={selected === option.value}
        />
      ))}
    </div>
  );
};

const Highlight = ({
  width,
  position,
  opacity,
  className,
}: {
  width: number;
  position: number;
  opacity: number;
  className?: string;
}) => (
  <motion.div
    className={twMerge("bg-brand-500 absolute h-[90%] rounded-md", className)}
    initial={{ opacity: 0 }}
    animate={{ x: position, width, opacity }}
    transition={{ type: "tween", duration: 0.2, opacity: { delay: 0.2 } }}
  />
);

const Segment = React.forwardRef<
  HTMLDivElement,
  { className: string; onClick: () => void; label: string; selected: boolean }
>(({ className, onClick, label, selected }, ref) => {
  const [initialRender, setInitialRender] = useState(true);

  useEffect(() => {
    setInitialRender(false);
  }, []);

  return (
    <motion.div
      ref={ref}
      className={className}
      onClick={onClick}
      tabIndex={0}
      initial={{ color: "black" }}
      animate={{ color: selected ? "white" : "black" }}
      transition={{
        type: "tween",
        duration: 0.2,
        delay: initialRender ? 0.2 : 0,
      }}
    >
      {label}
    </motion.div>
  );
});
Segment.displayName = "Segment";

export default SegmentedControl;
